// Helpers for "song mix" Section parts and per-section part numbering.
// A Section part (name === 'Section') is a divider between songs in a mix; its
// title is stored in part.lyrics. Part numbers reset after each Section marker.

export const isSectionPart = (part) => part?.name === 'Section';

// Returns a map { [partId]: displayNumber } for rendering part titles.
// Display always follows each part's stored number, so a part keeps its number
// when reordered within its section (the number belongs to the part, not its
// position). Crossing a section boundary is what renumbers a part — see
// renumberParts, which persists the new numbers at move time.
export const computePartNumbers = (parts = []) => {
  const numbers = {};

  parts.forEach((part) => {
    // Duplicates mirror their original's number (its stored value may have
    // changed after the duplicate was created, e.g. a cross-section move).
    if (part.isDuplicate && numbers[part.duplicateId] != null) {
      numbers[part.id] = numbers[part.duplicateId];
      return;
    }
    numbers[part.id] = part.number;
  });

  return numbers;
};

// Number of Section markers before the given index — i.e. which "song" in the
// mix a part at that index belongs to. Used to detect when a move crosses a
// section boundary.
export const sectionIndexAt = (parts = [], index) => {
  let count = 0;
  for (let i = 0; i < index && i < parts.length; i += 1) {
    if (isSectionPart(parts[i])) count += 1;
  }
  return count;
};

// Persists per-section positional numbering into each part's stored `number`.
// Numbering resets after every Section marker; duplicates inherit their
// original's number and don't advance the counter. Notes are left untouched —
// they keep their own global numbering via SongForm's sortNoteParts.
// Call this only when a part has actually changed sections; a within-section
// reorder must leave stored numbers alone.
export const renumberParts = (parts = []) => {
  let counters = {};

  parts.forEach((part) => {
    if (isSectionPart(part)) {
      counters = {}; // reset numbering for the next song in the mix
      return;
    }

    if (part.name === 'Note') return;

    if (part.isDuplicate) {
      const original = parts.find((p) => p.id === part.duplicateId);
      if (original && original.number != null) {
        part.number = original.number;
        return;
      }
    }

    counters[part.name] = (counters[part.name] || 0) + 1;
    part.number = counters[part.name];
  });

  return parts;
};

// Highest existing number for a part name. When the song contains Sections, only
// counts occurrences after the last Section marker (new parts append to the end).
export const getNextPartNumber = (parts = [], partName) => {
  let relevantParts = parts;

  const lastSectionIndex = parts.map(isSectionPart).lastIndexOf(true);
  if (lastSectionIndex !== -1) {
    relevantParts = parts.slice(lastSectionIndex + 1);
  }

  let maxPartNumber = 0;
  relevantParts
    .filter((part) => part.name === partName)
    .forEach((part) => {
      if (part.number > maxPartNumber) {
        maxPartNumber = parseInt(part.number);
      }
    });

  return maxPartNumber + 1;
};

// True when the part falls under a Section marker — i.e. a Section appears before it
// in the list. Parts before the first Section marker aren't considered "in a section".
export const isPartInSection = (parts = [], partId) => {
  const index = parts.findIndex((p) => p.id === partId);
  if (index === -1) return false;
  return parts.slice(0, index).some(isSectionPart);
};

// Splice index marking the end of the section that contains the given part: the
// position of the next Section marker after it, or the end of the list. A part
// appended to its section is inserted here.
export const getSectionEndIndex = (parts = [], partId) => {
  const index = parts.findIndex((p) => p.id === partId);
  if (index === -1) return parts.length;

  for (let i = index + 1; i < parts.length; i += 1) {
    if (isSectionPart(parts[i])) return i;
  }
  return parts.length;
};
